import { BaseFunctionService } from "./BaseFunctionService";

export interface IItemVenda {
  id_produto: number;
  quantidade: number;
  valor_unitario: number;
}

export interface IVendaResult {
  success: boolean;
  message: string;
  id_venda?: number;
  numero_venda?: number;
  valor_total?: number;
  valor_final?: number;
  desconto?: number;
  saldo_restante?: number;
  saldo_anterior_aluno?: number;
  saldo_atual_aluno?: number;
  itens_processados?: number;
  restricoes?: any[]; // Para resultados com restrições
  tipo_comprador?: string; // Novo campo: 'Aluno' ou 'Funcionário'
  nome_comprador?: string; // Novo campo
  id_responsavel?: number | null; // Novo campo (NULL para funcionários)
  saldo_disponivel?: number; // Para casos de erro de saldo
  valor_compra?: number; // Para casos de erro de saldo
}

/**
 * Serviço para funções relacionadas a vendas
 */
export class VendaFunctionsService extends BaseFunctionService {
  /**
   * Processa uma venda completa (pessoa, itens, pagamento)
   * ✅ Funciona para ALUNOS e FUNCIONÁRIOS automaticamente
   * ✅ Detecta o tipo de pessoa e usa a tabela de saldo correta:
   *    - Alunos (tipo 5): usa saldos_alunos
   *    - Funcionários (tipo 7): usa saldos_funcionarios
   */
  async processarVenda(
    idPessoa: number,
    idOperador: number,
    idOrganizacao: number,
    itens: IItemVenda[],
    desconto: number = 0,
    observacoes?: string,
  ): Promise<IVendaResult> {
    try {
      // Validação de entrada
      if (!itens || itens.length === 0) {
        throw new Error("Lista de itens não pode estar vazia");
      }

      if (!idPessoa || !idOperador || !idOrganizacao) {
        throw new Error(
          "IDs de pessoa, operador e organização são obrigatórios",
        );
      }

      // Validação individual dos itens
      for (const item of itens) {
        if (!item.id_produto || item.quantidade <= 0) {
          throw new Error(
            "Itens devem ter produto válido e quantidade positiva",
          );
        }
      }

      console.log("📦 Processando venda:", {
        pessoa: idPessoa,
        operador: idOperador,
        organizacao: idOrganizacao,
        itens: itens.length,
        valorEstimado: itens.reduce(
          (acc, item) => acc + item.valor_unitario * item.quantidade,
          0,
        ),
      });

      // ✅ USAR fn_processar_venda PARA FUNCIONAR COM ALUNOS E FUNCIONÁRIOS
      // Esta função detecta automaticamente o tipo (aluno/funcionário) e usa a tabela de saldo correta
      const { data: resultado, error } = await this.client.rpc(
        "fn_processar_venda",
        {
          p_id_pessoa: idPessoa,
          p_id_operador: idOperador,
          p_id_organizacao: idOrganizacao,
          p_itens: itens as any, // Cast para JSON devido à ambiguidade de tipos
          p_desconto: desconto || 0,
          p_observacoes: observacoes || undefined,
        },
      );

      if (error) {
        throw new Error(`Erro na função de venda: ${error.message}`);
      }

      console.log(
        "✅ Venda processada com detecção automática de tipo (aluno/funcionário):",
        resultado,
      );
      return resultado as unknown as IVendaResult;
    } catch (error) {
      console.error("❌ Erro ao processar venda:", error);

      // Tratamento de erro amigável
      const errorMessage =
        error instanceof Error ? error.message : "Erro desconhecido";

      return {
        success: false,
        message: `Erro ao processar venda: ${errorMessage}`,
      };
    }
  }

  /**
   * ⚠️ MÉTODO LEGADO - Processa venda COM validação de responsável
   * ⚠️ FUNCIONÁRIOS NÃO FUNCIONAM CORRETAMENTE COM ESTE MÉTODO
   * Mantido para compatibilidade, mas use processarVenda() ao invés deste
   */
  async processarVendaComResponsavel(
    idPessoa: number,
    idOperador: number,
    idOrganizacao: number,
    itens: IItemVenda[],
    desconto: number = 0,
    observacoes?: string,
  ): Promise<IVendaResult> {
    try {
      // Validação de entrada
      if (!itens || itens.length === 0) {
        throw new Error("Lista de itens não pode estar vazia");
      }

      if (!idPessoa || !idOperador || !idOrganizacao) {
        throw new Error(
          "IDs de pessoa, operador e organização são obrigatórios",
        );
      }

      console.log("📦 Processando venda com validação de responsável:", {
        pessoa: idPessoa,
        operador: idOperador,
        organizacao: idOrganizacao,
        itens: itens.length,
      });

      // Usar função original que valida responsável
      const { data: resultado, error } = await this.client.rpc(
        "fn_processar_venda",
        {
          p_id_pessoa: idPessoa,
          p_id_operador: idOperador,
          p_id_organizacao: idOrganizacao,
          p_itens: itens as any,
          p_desconto: desconto || 0,
          p_observacoes: observacoes || undefined,
        },
      );

      if (error) {
        throw new Error(`Erro na função de venda: ${error.message}`);
      }

      console.log(
        "✅ Venda processada com validação de responsável:",
        resultado,
      );
      return resultado as unknown as IVendaResult;
    } catch (error) {
      console.error("❌ Erro ao processar venda com responsável:", error);

      const errorMessage =
        error instanceof Error ? error.message : "Erro desconhecido";

      return {
        success: false,
        message: `Erro ao processar venda: ${errorMessage}`,
      };
    }
  }
}

// Instância singleton
export const vendaFunctionsService = new VendaFunctionsService();
